/** 1 item of the nav tree of a mkdocs site. */
export type NavNode = {
  label: string;
  /** The page path inside the site, `''` for the site home. */
  path?: string;
  children?: NavNode[];
};

// A placeholder origin to resolve the relative links of a page. Only the path
// of the result is used.
const SITE_ORIGIN = 'https://site.invalid';

/**
 * Reads the nav tree of a page that the mkdocs-material theme built. The
 * TechDocs metadata of a site has no nav: the `include_dir_to_nav` plugin
 * builds the nav at build time, so the HTML of a page is the only source.
 * `pagePath` is the path of the page inside the site, because the nav links
 * are relative to it. Returns an empty tree when the page has no nav that
 * this parser can read, for example after a theme change.
 */
export function parseMkdocsNav(html: string, pagePath = ''): NavNode[] {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const list = document.querySelector('nav.md-nav--primary > ul.md-nav__list');
  if (!list) {
    return [];
  }

  // mkdocs builds directory URLs, so every page is a folder.
  const pageUrl = `${SITE_ORIGIN}/${pagePath ? `${pagePath}/` : ''}`;
  return parseNavList(list, pageUrl);
}

function parseNavList(list: Element, pageUrl: string): NavNode[] {
  return Array.from(list.querySelectorAll(':scope > li.md-nav__item'))
    .map(item => parseNavItem(item, pageUrl))
    .filter((node): node is NavNode => node !== undefined);
}

function parseNavItem(item: Element, pageUrl: string): NavNode | undefined {
  // A group has a `label` toggle. The open page also has one, for its table of
  // contents, and its link comes after it.
  const link = item.querySelector(':scope > a.md-nav__link');
  const labelElement =
    item.querySelector(':scope > label.md-nav__link') ?? link;
  const label = getText(labelElement);
  // The open page holds its table of contents in a `md-nav--secondary` nav.
  // It is not a part of the site nav.
  const childList = item.querySelector(
    ':scope > nav.md-nav:not(.md-nav--secondary) > ul.md-nav__list',
  );
  const children = childList ? parseNavList(childList, pageUrl) : [];
  const path = getPagePath(link?.getAttribute('href'), pageUrl);

  if (!label || (path === undefined && children.length === 0)) {
    return undefined;
  }

  return {
    label,
    ...(path !== undefined && { path }),
    ...(children.length > 0 && { children }),
  };
}

function getText(element: Element | null): string {
  const text =
    element?.querySelector('.md-ellipsis')?.textContent ??
    element?.textContent ??
    '';
  return text.replace(/\s+/g, ' ').trim();
}

/** The path inside the site, or `undefined` for a link that leaves the site. */
function getPagePath(
  href: string | null | undefined,
  pageUrl: string,
): string | undefined {
  if (!href || href.startsWith('#')) {
    return undefined;
  }

  const url = new URL(href, pageUrl);
  if (url.origin !== SITE_ORIGIN) {
    return undefined;
  }

  return url.pathname.replace(/index\.html$/, '').replace(/^\/+|\/+$/g, '');
}
